import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Upload, FileText, Check, AlertCircle, ArrowRight, ArrowLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const STEPS = ['Quelle', 'Vorschau', 'Feldzuordnung', 'Übernehmen'];

export default function AdminImport() {
  const [step, setStep] = useState(0);
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState('');
  const [sourceType, setSourceType] = useState('paste'); // paste | url
  const [pasteData, setPasteData] = useState('');
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState('none');
  const [authValue, setAuthValue] = useState('');
  const [format, setFormat] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [rawData, setRawData] = useState('');
  const [mapping, setMapping] = useState({ tld: '', registration: '', renewal: '', transfer: '' });
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.getProviders().then(setProviders).catch(console.error); }, []);

  const handlePreview = async () => {
    setLoading(true);
    try {
      let result;
      if (sourceType === 'paste') {
        result = await api.importPreview(pasteData, format || null);
        setRawData(pasteData);
      } else {
        const fetchResult = await api.importFetch({
          provider_id: providerId, url, format: format || null,
          auth_type: authType !== 'none' ? authType : null,
          auth_value: authType !== 'none' ? authValue : null
        });
        result = fetchResult;
        setRawData(fetchResult.raw_data || '');
      }
      setPreviewData(result);
      // Auto-detect mapping
      if (result.available_fields) {
        const fields = result.available_fields.map(f => f.toLowerCase());
        const newMapping = { tld: '', registration: '', renewal: '', transfer: '' };
        for (const f of result.available_fields) {
          const fl = f.toLowerCase();
          if (['tld', 'domain', 'extension', 'domain_extension', 'endung'].includes(fl)) newMapping.tld = f;
          if (['registration', 'reg_price', 'register', 'reg', 'registrierung', 'preis_reg'].includes(fl)) newMapping.registration = f;
          if (['renewal', 'renew_price', 'renew', 'verlaengerung', 'preis_renew'].includes(fl)) newMapping.renewal = f;
          if (['transfer', 'transfer_price', 'xfer', 'preis_transfer'].includes(fl)) newMapping.transfer = f;
        }
        setMapping(newMapping);
      }
      setStep(1);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const result = await api.importApply({
        provider_id: providerId,
        data: rawData,
        mapping,
        format: previewData?.format || null
      });
      setImportResult(result);
      
      // Wenn URL-basiert: Import-Quelle mit Mapping speichern für automatische 24h-Prüfung
      if (sourceType === 'url' && url) {
        try {
          await api.saveImportSource({
            provider_id: providerId,
            url,
            format: previewData?.format || null,
            auth_type: authType !== 'none' ? authType : null,
            auth_value: authType !== 'none' ? authValue : null,
            field_mapping: mapping,
            auto_update: true
          });
          toast.success(`${result.imported} Preise importiert. URL wird automatisch alle 24h geprüft.`);
        } catch (saveErr) {
          toast.success(`${result.imported} Preise importiert (URL-Speicherung fehlgeschlagen: ${saveErr.message})`);
        }
      } else {
        toast.success(`${result.imported} Preise importiert`);
      }
      setStep(3);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep(0); setPreviewData(null); setImportResult(null); setRawData(''); setPasteData('');
    setUrl(''); setMapping({ tld: '', registration: '', renewal: '', transfer: '' });
  };

  return (
    <div data-testid="import-wizard">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Preisliste importieren</h1>
        <p className="text-sm text-muted-foreground mt-1">Preislisten von Anbietern importieren und Feldzuordnungen erstellen</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="admin-import-wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
              i === step ? 'bg-sky-50 text-sky-700' : i < step ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
            }`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-sky-600 text-white' : 'bg-slate-300 text-white'
              }`}>{i < step ? '✓' : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 0: Source */}
      {step === 0 && (
        <Card className="rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Anbieter *</Label>
              <Select value={providerId} onValueChange={setProviderId}>
                <SelectTrigger><SelectValue placeholder="Anbieter wählen" /></SelectTrigger>
                <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Datenquelle</Label>
              <div className="flex gap-2 mt-1">
                <Button variant={sourceType === 'paste' ? 'default' : 'outline'} size="sm" onClick={() => setSourceType('paste')}>Einfügen</Button>
                <Button variant={sourceType === 'url' ? 'default' : 'outline'} size="sm" onClick={() => setSourceType('url')}>URL</Button>
              </div>
            </div>
            {sourceType === 'paste' ? (
              <div>
                <Label>Daten (CSV, JSON oder XML)</Label>
                <Textarea
                  value={pasteData}
                  onChange={e => setPasteData(e.target.value)}
                  placeholder={'tld,registration,renewal,transfer\nde,5.99,8.99,6.99\ncom,9.99,12.99,10.99'}
                  rows={10}
                  className="font-mono text-xs"
                />
              </div>
            ) : (
              <>
                <div><Label>URL</Label><Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://provider.com/pricelist.csv" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Authentifizierung</Label>
                    <Select value={authType} onValueChange={setAuthType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Keine</SelectItem>
                        <SelectItem value="bearer">Bearer Token</SelectItem>
                        <SelectItem value="api_key">API-Key</SelectItem>
                        <SelectItem value="basic">Basic Auth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {authType !== 'none' && (
                    <div><Label>Auth-Wert</Label><Input value={authValue} onChange={e => setAuthValue(e.target.value)} placeholder="Token/Key" type="password" /></div>
                  )}
                </div>
              </>
            )}
            <div>
              <Label>Format (optional)</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger><SelectValue placeholder="Auto-Erkennung" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-Erkennung</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="xml">XML</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={handlePreview} disabled={!providerId || (sourceType === 'paste' ? !pasteData : !url) || loading} className="gap-2" data-testid="import-wizard-next-button">
                {loading ? 'Lade...' : 'Vorschau'} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Preview */}
      {step === 1 && previewData && (
        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-sky-600" />
              <span className="font-semibold">Vorschau</span>
              <Badge variant="outline">{previewData.format?.toUpperCase()}</Badge>
              <Badge variant="outline">{previewData.total_rows} Zeilen</Badge>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {previewData.available_fields?.map(f => (
                      <th key={f} className="px-3 py-2 text-left font-medium text-muted-foreground">{f}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.preview_rows?.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      {previewData.available_fields?.map(f => (
                        <td key={f} className="px-3 py-2 tabular-nums">{row[f] ?? ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(0)} className="gap-2" data-testid="import-wizard-back-button"><ArrowLeft className="w-4 h-4" /> Zurück</Button>
              <Button onClick={() => setStep(2)} className="gap-2" data-testid="import-wizard-next-button">Feldzuordnung <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Field Mapping */}
      {step === 2 && previewData && (
        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Feldzuordnung</h3>
            <p className="text-sm text-muted-foreground mb-4">Ordnen Sie die Felder der Preisliste unseren Feldern zu.</p>
            <div className="space-y-3" data-testid="field-mapping-table">
              {[{key: 'tld', label: 'TLD *', required: true}, {key: 'registration', label: 'Registrierung'}, {key: 'renewal', label: 'Verlängerung'}, {key: 'transfer', label: 'Transfer'}].map(field => (
                <div key={field.key} className="grid grid-cols-2 gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${field.required && !mapping[field.key] ? 'text-red-600' : 'text-slate-700'}`}>{field.label}</span>
                    {mapping[field.key] && <Check className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <Select value={mapping[field.key]} onValueChange={v => setMapping({...mapping, [field.key]: v})}>
                    <SelectTrigger className={!mapping[field.key] && field.required ? 'border-red-300' : ''}>
                      <SelectValue placeholder="Feld wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-- Nicht zuordnen --</SelectItem>
                      {previewData.available_fields?.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="gap-2" data-testid="import-wizard-back-button"><ArrowLeft className="w-4 h-4" /> Zurück</Button>
              <Button onClick={handleApply} disabled={!mapping.tld || loading} className="gap-2" data-testid="import-wizard-apply-button">
                {loading ? 'Importiere...' : 'Import starten'} <Upload className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Result */}
      {step === 3 && importResult && (
        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Import abgeschlossen</h3>
                <p className="text-sm text-muted-foreground">{importResult.imported} Preise erfolgreich importiert</p>
              </div>
            </div>
            {importResult.new_tlds?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-1">Neue TLDs erstellt:</p>
                <div className="flex flex-wrap gap-1">
                  {importResult.new_tlds.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                </div>
              </div>
            )}
            {importResult.errors?.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-1">Warnungen:</p>
                {importResult.errors.map((e, i) => <p key={i} className="text-xs text-amber-700">{e}</p>)}
              </div>
            )}
            {sourceType === 'url' && url && (
              <div className="mb-4 p-3 rounded-lg bg-sky-50 border border-sky-200">
                <p className="text-sm font-medium text-sky-800">Automatische Prüfung aktiv</p>
                <p className="text-xs text-sky-700 mt-1">Die URL wird alle 24 Stunden automatisch auf Änderungen geprüft und Preise werden aktualisiert.</p>
              </div>
            )}
            <Button onClick={reset} className="gap-2">
              Weiteren Import starten <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
